// Shared state for the guide's sections: the location model, language,
// dialling, presence and hash-based navigation.

import { createContext, useContext } from 'react';
import type { CallFlow } from '../../api/client';
import type { DialTarget } from '../CallFlowsViewer';
import type { GuideModel } from './model';
import type { I18nKey, Lang } from './i18n';
import type { PresenceInfo } from './lib/callFlowStatus';

export type { DialTarget };

export type GuideSection = 'start' | 'callflow' | 'departments' | 'people' | 'hours' | 'structure' | 'about';
export interface GuideRoute { section: GuideSection; id?: string }

export type TFn = (key: I18nKey, vars?: Record<string, string | number>) => string;

export interface GuideContextValue {
  flow: CallFlow;
  model: GuideModel;
  lang: Lang;
  t: TFn;
  accent: string;
  dirById: Record<string, DialTarget>;
  onDial: (target: DialTarget) => void;
  /** 'teams' inside the Teams tab, 'web' on the standalone page. */
  dialMode: 'teams' | 'web';
  presence?: Map<string, PresenceInfo>;
  presenceState?: 'ok' | 'consent' | 'unavailable' | 'loading';
  /** Admin-consent link shown to admins while presence is not enabled. */
  presenceConsentUrl?: string;
  /** Ticks every minute so open/closed badges stay current. */
  now: Date;
  route: GuideRoute;
  navigate: (route: GuideRoute) => void;
  isAdmin: boolean;
}

export const GuideContext = createContext<GuideContextValue | null>(null);

export function useGuide(): GuideContextValue {
  const v = useContext(GuideContext);
  if (!v) throw new Error('useGuide outside PhoneMenuGuide');
  return v;
}

const SECTIONS: GuideSection[] = ['start', 'callflow', 'departments', 'people', 'hours', 'structure', 'about'];

/** `#callflow/<nodeId>` ⇄ { section, id }. Unknown hashes land on Start. */
export function parseHash(hash: string): GuideRoute {
  const raw = (hash || '').replace(/^#/, '');
  const [section, ...rest] = raw.split('/');
  const id = rest.length ? decodeURIComponent(rest.join('/')) : undefined;
  if (SECTIONS.includes(section as GuideSection)) return { section: section as GuideSection, id: id || undefined };
  return { section: 'start' };
}

export function routeToHash(route: GuideRoute): string {
  return `#${route.section}${route.id ? `/${encodeURIComponent(route.id)}` : ''}`;
}
